/**
 * @typedef {Object} AnchorEntry
 * @property {string} id
 * @property {string} label
 * @property {HTMLElement} target
 * @property {HTMLAnchorElement} link
 */

const ACTIVE_CLASS = 'product-anchor-nav__link--active'
const LINK_CLASS = 'product-anchor-nav__link'
const HEADER_BUFFER = 24
const VISIBILITY_HYSTERESIS = 48
const PAGE_END_THRESHOLD = 8
const TRACK_INTERACTION_DELAY = 140
const ACTIVE_SECTION_LINE_FROM_BOTTOM = 0.3
const ANCHOR_CLICK_EXTRA_VIEWPORT_INSET = 60

// Coalesce widget-driven DOM churn (Judge.me, Alma, Trustpilot, bundle picker)
// before rebuilding anchors, and stop watching once the page settles.
// Exported so tests can drive fake timers without duplicating these values.
export const CONTENT_MUTATION_DEBOUNCE = 350
export const CONTENT_OBSERVER_IDLE_TIMEOUT = 6000

// Bounded rescans that catch anchors added after the content observer's idle
// disconnect (e.g. a slow-hydrating Judge.me reviews root), without keeping a
// `MutationObserver` alive indefinitely.
const FALLBACK_RESCAN_DELAYS_MS = [1000, 3000, 8000]

import {
  getPageScrollY,
  isPageScrollLocked,
  navigationMayRestoreScroll,
  ScrollLockChangeEvent
} from '@theme/dialog'

/**
 * @param {string} text
 * @returns {string}
 */
export const slugify = (text) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

/**
 * Whether an added/removed node can change the anchor set (an `h2`, a subtree
 * containing one, or the explicit reviews anchor target). Everything else —
 * the constant DOM churn from third-party widgets — is ignored so it never
 * retriggers a rebuild.
 *
 * @param {Node} node
 * @param {string} [reviewsId]
 * @returns {boolean}
 */
export const nodeAffectsAnchors = (node, reviewsId = '') => {
  if (!(node instanceof Element)) return false
  if (node.tagName === 'H2') return true
  if (typeof node.querySelector === 'function' && node.querySelector('h2')) {
    return true
  }
  if (reviewsId) {
    if (node.id === reviewsId) return true
    if (
      typeof node.querySelector === 'function' &&
      node.querySelector(`#${CSS.escape(reviewsId)}`)
    ) {
      return true
    }
  }
  return false
}

/**
 * @param {HTMLElement} heading
 * @param {string} sectionClass
 * @returns {boolean}
 */
const isInsideProductInfoSection = (heading, sectionClass) =>
  !!heading.closest(`.${sectionClass}`)

/**
 * @param {HTMLElement} heading
 * @returns {HTMLElement | null}
 */
const resolveScrollTarget = (heading) => {
  if (heading.id) return heading

  let ancestor = heading.parentElement

  while (ancestor && ancestor.id !== 'MainContent') {
    if (ancestor.id) return ancestor
    ancestor = ancestor.parentElement
  }

  const text = heading.textContent?.trim()
  const baseSlug = slugify(text || '')
  if (!baseSlug) return null

  let candidateId = `anchor-${baseSlug}`
  let index = 2

  while (true) {
    const existing = document.getElementById(candidateId)
    if (!existing || existing === heading) break
    candidateId = `anchor-${baseSlug}-${index}`
    index += 1
  }

  heading.id = candidateId
  return heading
}

/**
 * Inserts `entry` into `targets` at the first index whose target comes after
 * `entry.target` in document order. Appends when no later target exists.
 *
 * @template {{ target: HTMLElement }} T
 * @param {T[]} targets
 * @param {T} entry
 * @returns {T[]}
 */
export const insertTargetInDocumentOrder = (targets, entry) => {
  const insertAt = targets.findIndex((existing) => {
    if (!(existing.target instanceof HTMLElement)) return false

    return Boolean(
      entry.target.compareDocumentPosition(existing.target) &
      Node.DOCUMENT_POSITION_FOLLOWING
    )
  })

  if (insertAt === -1) {
    targets.push(entry)
    return targets
  }

  targets.splice(insertAt, 0, entry)
  return targets
}

/**
 * Scans `main` for anchor-nav targets: page `h2` headings (excluding the
 * product info section and any `data-anchor-nav-exclude` subtree) plus an
 * optional reviews anchor inserted at its document position. Exported so
 * discovery can be exercised in tests without mounting the full custom element.
 *
 * @param {HTMLElement} main
 * @param {{ sectionClass?: string, reviewsId?: string, reviewsLabel?: string }} [options]
 * @returns {{ id: string, label: string, target: HTMLElement }[]}
 */
export const collectAnchorTargets = (main, options = {}) => {
  const sectionClass = options.sectionClass || 'product-information'
  const reviewsId = options.reviewsId?.trim() || ''
  const reviewsLabel = options.reviewsLabel?.trim() || ''

  const headings = main.querySelectorAll('h2')
  /** @type {{ id: string, label: string, target: HTMLElement }[]} */
  const validTargets = []
  const seenIds = new Set()

  for (const heading of headings) {
    if (!(heading instanceof HTMLElement)) continue
    if (isInsideProductInfoSection(heading, sectionClass)) continue
    if (heading.hasAttribute('data-anchor-nav-exclude')) continue
    if (heading.closest('[data-anchor-nav-exclude]')) continue

    const label =
      heading.dataset.anchorNavLabel?.trim() || heading.textContent?.trim()
    if (!label) continue

    const target = resolveScrollTarget(heading)
    if (!(target instanceof HTMLElement) || !target.id) continue
    if (seenIds.has(target.id)) continue

    seenIds.add(target.id)
    validTargets.push({ id: target.id, label, target })
  }

  if (reviewsId) {
    // Dedupe Judge.me h2s that resolve to the reviews wrapper, then insert
    // the reviews entry at its real document position (not forced last).
    for (let i = validTargets.length - 1; i >= 0; i--) {
      if (validTargets[i]?.id === reviewsId) validTargets.splice(i, 1)
    }

    const reviewsTarget = document.getElementById(reviewsId)
    if (reviewsTarget instanceof HTMLElement) {
      insertTargetInDocumentOrder(validTargets, {
        id: reviewsId,
        label: reviewsLabel || 'Reviews',
        target: reviewsTarget
      })
    }
  }

  return validTargets
}

/**
 * Decides whether a freshly scanned anchor target list should replace the
 * current link list. Guards against transient scans that briefly see fewer
 * anchors while third-party widgets (Judge.me, Alma, bundle picker) are
 * mid-mutation: a reduced result is withheld once and only applied once the
 * exact same reduced signature is observed again on a later scan. Growth and
 * same-size changes always apply immediately.
 *
 * @param {{ id: string, label: string }[]} currentEntries
 * @param {{ id: string, label: string }[]} newTargets
 * @param {string | null} pendingShrinkSignature
 * @returns {{ apply: boolean, signature: string, pendingShrinkSignature: string | null }}
 */
export const resolveAnchorRebuild = (
  currentEntries,
  newTargets,
  pendingShrinkSignature
) => {
  const signature = newTargets.map((e) => `${e.id}:${e.label}`).join('|')
  const isShrink =
    currentEntries.length > 0 && newTargets.length < currentEntries.length

  if (!isShrink) {
    return { apply: true, signature, pendingShrinkSignature: null }
  }

  if (pendingShrinkSignature === signature) {
    return { apply: true, signature, pendingShrinkSignature: null }
  }

  return { apply: false, signature, pendingShrinkSignature: signature }
}

class ProductAnchorNav extends HTMLElement {
  /** @type {AnchorEntry[]} */
  #entries = []

  /** @type {MutationObserver | null} */
  #contentObserver = null

  /** @type {ResizeObserver | null} */
  #resizeObserver = null

  /** @type {HTMLElement | null} */
  #nav = null

  /** @type {HTMLElement | null} */
  #viewport = null

  /** @type {boolean} */
  #initQueued = false

  /** @type {number | null} */
  #queuedInit = null

  /** @type {boolean} */
  #updateQueued = false

  /** @type {number | null} */
  #queuedUpdate = null

  /** @type {number | null} */
  #trackInteractionTimer = null

  /** @type {number | null} */
  #trackAutoScrollTimer = null

  /** @type {number | null} */
  #pageScrollTimer = null

  /** @type {number | null} */
  #contentReinitTimer = null

  /** @type {number | null} */
  #contentObserverIdleTimer = null

  /** @type {number[]} */
  #fallbackRescanTimers = []

  /** @type {boolean} */
  #fallbackRescansScheduled = false

  /** @type {string | null} */
  #pendingShrinkSignature = null

  /** @type {string | null} */
  #activeId = null

  /** @type {number | null} */
  #revealScrollY = null

  /**
   * Geometry reads force a full-page layout, and `<main>` resizes many times
   * while the page loads. The bar starts hidden, so nothing is measured until
   * the visitor scrolls or lands on a hash.
   * @type {boolean}
   */
  #hasUserScrolled = false

  /** @type {boolean} */
  #isTrackInteracting = false

  /** @type {boolean} */
  #activeChangedDuringTrackInteraction = false

  /** @type {boolean} */
  #isDragging = false

  /** @type {boolean} */
  #hasDragged = false

  /** @type {number} */
  #startX = 0

  /** @type {number} */
  #scrollLeft = 0

  /**
   * When the user manually scrolls the horizontal nav track, we avoid
   * auto-centering the active link (which causes an annoying snap-back).
   * We clear this flag when the active section changes due to page scrolling.
   * @type {boolean}
   */
  #trackPositionDirty = false

  #handleWindowScroll = () => {
    if (isPageScrollLocked()) return
    this.#hasUserScrolled = true
    this.#queueUpdate()
  }

  #handleWindowResize = () => {
    this.#revealScrollY = null // Force recalculation
    this.#queueUpdate()
  }

  #handleHashChange = () => {
    this.#hasUserScrolled = true
    this.#queueUpdate()
  }

  #needsGeometry() {
    return (
      this.#hasUserScrolled ||
      window.location.hash.length > 1 ||
      !this.hasAttribute('hidden') ||
      navigationMayRestoreScroll()
    )
  }

  /** @param {Event} event */
  /** @type {boolean} */
  #syncLinkScrollWithoutMotion = false

  #handleScrollLockChange = (event) => {
    const detail = /** @type {{ locked?: boolean } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    )
    if (detail?.locked) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncLinkScrollWithoutMotion = true
        this.#queueUpdate()
      })
    })
  }

  #handleViewportScroll = () => {
    if (this.#trackAutoScrollTimer) return

    this.#isTrackInteracting = true
    this.#trackPositionDirty = true

    if (this.#trackInteractionTimer) {
      window.clearTimeout(this.#trackInteractionTimer)
    }

    this.#trackInteractionTimer = window.setTimeout(() => {
      this.#trackInteractionTimer = null
      this.#isTrackInteracting = false
      if (this.#activeChangedDuringTrackInteraction) {
        this.#activeChangedDuringTrackInteraction = false
        this.#ensureActiveLinkVisible('smooth')
      }
    }, TRACK_INTERACTION_DELAY)
  }

  /**
   * @param {MouseEvent} e
   */
  #handleMouseDown = (e) => {
    if (!this.#viewport || !this.#nav) return
    this.#isDragging = true
    this.#hasDragged = false
    this.#startX = e.pageX - this.#viewport.offsetLeft
    this.#scrollLeft = this.#viewport.scrollLeft
    this.#nav.classList.add('product-anchor-nav__track--dragging')
  }

  #handleMouseLeave = () => {
    this.#isDragging = false
    this.#nav?.classList.remove('product-anchor-nav__track--dragging')
  }

  #handleMouseUp = () => {
    this.#isDragging = false
    this.#nav?.classList.remove('product-anchor-nav__track--dragging')
  }

  /**
   * @param {MouseEvent} e
   */
  #handleMouseMove = (e) => {
    if (!this.#isDragging || !this.#viewport) return

    const x = e.pageX - this.#viewport.offsetLeft
    const walk = (x - this.#startX) * 1.5 // Scroll speed multiplier

    // Mark as dragged if we moved more than 5px
    if (Math.abs(x - this.#startX) > 5) {
      this.#hasDragged = true
      e.preventDefault() // Prevent ghost dragging of links
    }

    this.#viewport.scrollLeft = this.#scrollLeft - walk
  }

  /**
   * @param {WheelEvent} e
   */
  #handleWheel = (e) => {
    if (!this.#viewport) return

    // Convert vertical wheel scrolls strictly into horizontal scrolling
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      const maxScroll = this.#viewport.scrollWidth - this.#viewport.clientWidth
      const atLeft = this.#viewport.scrollLeft <= 0 && e.deltaY < 0
      const atRight = this.#viewport.scrollLeft >= maxScroll - 1 && e.deltaY > 0

      if (!atLeft && !atRight) {
        e.preventDefault()
        this.#viewport.scrollLeft += e.deltaY
      }
    }
  }

  connectedCallback() {
    this.#viewport = this.querySelector('.product-anchor-nav__viewport')
    this.#nav = this.querySelector('.product-anchor-nav__track')
    if (!this.#viewport || !this.#nav) return

    window.addEventListener('scroll', this.#handleWindowScroll, {
      passive: true
    })
    window.addEventListener('resize', this.#handleWindowResize)
    window.addEventListener('hashchange', this.#handleHashChange)
    document.addEventListener(
      ScrollLockChangeEvent.eventName,
      this.#handleScrollLockChange
    )
    this.#viewport.addEventListener('scroll', this.#handleViewportScroll, {
      passive: true
    })

    this.#viewport.addEventListener('mousedown', this.#handleMouseDown)
    this.#viewport.addEventListener('mouseleave', this.#handleMouseLeave)
    this.#viewport.addEventListener('mouseup', this.#handleMouseUp)
    this.#viewport.addEventListener('mousemove', this.#handleMouseMove)
    this.#viewport.addEventListener('wheel', this.#handleWheel, {
      passive: false
    })

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          this.#queueInit()
        },
        { once: true }
      )
    } else {
      this.#queueInit()
    }
  }

  disconnectedCallback() {
    if (this.#queuedInit) {
      cancelAnimationFrame(this.#queuedInit)
      this.#queuedInit = null
    }
    this.#initQueued = false

    if (this.#queuedUpdate) {
      cancelAnimationFrame(this.#queuedUpdate)
      this.#queuedUpdate = null
    }
    this.#updateQueued = false

    if (this.#trackInteractionTimer) {
      window.clearTimeout(this.#trackInteractionTimer)
      this.#trackInteractionTimer = null
    }

    if (this.#trackAutoScrollTimer) {
      window.clearTimeout(this.#trackAutoScrollTimer)
      this.#trackAutoScrollTimer = null
    }

    if (this.#pageScrollTimer) {
      window.clearTimeout(this.#pageScrollTimer)
      this.#pageScrollTimer = null
    }

    if (this.#contentReinitTimer) {
      window.clearTimeout(this.#contentReinitTimer)
      this.#contentReinitTimer = null
    }

    if (this.#contentObserverIdleTimer) {
      window.clearTimeout(this.#contentObserverIdleTimer)
      this.#contentObserverIdleTimer = null
    }

    for (const timer of this.#fallbackRescanTimers) {
      window.clearTimeout(timer)
    }
    this.#fallbackRescanTimers = []
    this.#fallbackRescansScheduled = false

    window.removeEventListener('scroll', this.#handleWindowScroll)
    window.removeEventListener('resize', this.#handleWindowResize)
    window.removeEventListener('hashchange', this.#handleHashChange)
    document.removeEventListener(
      ScrollLockChangeEvent.eventName,
      this.#handleScrollLockChange
    )
    this.#viewport?.removeEventListener('scroll', this.#handleViewportScroll)

    this.#viewport?.removeEventListener('mousedown', this.#handleMouseDown)
    this.#viewport?.removeEventListener('mouseleave', this.#handleMouseLeave)
    this.#viewport?.removeEventListener('mouseup', this.#handleMouseUp)
    this.#viewport?.removeEventListener('mousemove', this.#handleMouseMove)
    this.#viewport?.removeEventListener('wheel', this.#handleWheel)

    this.#contentObserver?.disconnect()
    this.#resizeObserver?.disconnect()
    this.#contentObserver = null
    this.#resizeObserver = null
  }

  #queueInit() {
    // Guard with a plain boolean set before scheduling: requestAnimationFrame
    // is expected to run asynchronously, but if anything ever invokes the
    // callback synchronously (e.g. a test stub), assigning the frame id to
    // `#queuedInit` only *after* the call returns would race with the
    // callback already having reset it, leaving the guard stuck "on".
    if (this.#initQueued) return
    this.#initQueued = true

    this.#queuedInit = requestAnimationFrame(() => {
      this.#initQueued = false
      this.#queuedInit = null
      this.#init()
    })
  }

  #queueUpdate() {
    if (this.#updateQueued) return
    this.#updateQueued = true

    this.#queuedUpdate = requestAnimationFrame(() => {
      this.#updateQueued = false
      this.#queuedUpdate = null
      this.#updateState()
    })
  }

  #init() {
    const main = document.querySelector('#MainContent')
    this.#observeContent() // Always heavily resilient: keep watching.

    if (!(main instanceof HTMLElement)) {
      this.#setVisible(false)
      return
    }

    this.#buildLinks(main)
    this.#scheduleFallbackRescans(main)

    if (this.#entries.length === 0) {
      this.#setVisible(false)
      return
    }

    if (this.#needsGeometry()) this.#measureRevealThreshold()
    this.#observeLayout(main)
    this.#queueUpdate()
  }

  /**
   * @param {HTMLElement} main
   */
  #buildLinks(main) {
    if (!this.#nav) return

    const sectionClass =
      this.dataset.productSectionClass || 'product-information'
    const reviewsId = this.dataset.reviewsAnchorId?.trim() || ''
    const reviewsLabel = this.dataset.reviewsAnchorLabel?.trim() || ''

    const validTargets = collectAnchorTargets(main, {
      sectionClass,
      reviewsId,
      reviewsLabel
    })

    // Compare with current entries to avoid unneeded rebuilds that cause "got remove" sensation
    const currentSignature = this.#entries
      .map((e) => `${e.id}:${e.label}`)
      .join('|')
    const newSignature = validTargets.map((e) => `${e.id}:${e.label}`).join('|')
    const nav = this.#nav

    const trackDomMatchesEntries =
      nav != null &&
      this.#entries.length > 0 &&
      nav.childElementCount === this.#entries.length &&
      this.#entries.every(
        (e) =>
          e.link instanceof HTMLElement &&
          e.link.isConnected &&
          e.link.parentElement === nav
      )

    if (currentSignature === newSignature && this.#entries.length > 0) {
      this.#pendingShrinkSignature = null
      if (trackDomMatchesEntries) {
        // Targets list hasn't effectively changed, just verify if target references altered
        for (let i = 0; i < validTargets.length; i++) {
          const entry = this.#entries[i]
          const vt = validTargets[i]
          if (entry && vt) entry.target = vt.target
        }
        return
      }
    }

    const rebuildDecision = resolveAnchorRebuild(
      this.#entries,
      validTargets,
      this.#pendingShrinkSignature
    )
    this.#pendingShrinkSignature = rebuildDecision.pendingShrinkSignature
    if (!rebuildDecision.apply) return

    // Build fresh links
    const fragment = document.createDocumentFragment()
    const entries = []

    for (const item of validTargets) {
      const link = document.createElement('a')
      link.href = `#${item.id}`
      link.className = LINK_CLASS
      link.textContent = item.label
      link.draggable = false
      link.addEventListener('click', (event) => {
        if (this.#hasDragged) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        this.#handleClick(event, item.target)
      })

      fragment.appendChild(link)
      entries.push({
        id: item.id,
        label: item.label,
        target: item.target,
        link
      })
    }

    this.#nav.replaceChildren(fragment)
    this.#entries = entries
  }

  #observeContent() {
    if (this.#contentObserver) return

    const reviewsId = this.dataset.reviewsAnchorId?.trim() || ''

    this.#contentObserver = new MutationObserver((mutations) => {
      // Ignore the incessant DOM churn from third-party widgets; only rebuild
      // when a mutation can actually change the anchor set.
      let affectsAnchors = false

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (nodeAffectsAnchors(node, reviewsId)) {
            affectsAnchors = true
            break
          }
        }
        if (affectsAnchors) break

        for (const node of mutation.removedNodes) {
          if (nodeAffectsAnchors(node, reviewsId)) {
            affectsAnchors = true
            break
          }
        }
        if (affectsAnchors) break
      }

      if (!affectsAnchors) return
      this.#scheduleContentReinit()
    })

    // Anchor targets only live in the main content region, so scoping the
    // observer there avoids reacting to header/footer/cart/modal/pixel churn.
    const observeRoot = document.getElementById('MainContent') || document.body
    this.#contentObserver.observe(observeRoot, {
      childList: true,
      subtree: true
    })

    this.#resetContentObserverIdleTimer()
  }

  #scheduleContentReinit() {
    this.#resetContentObserverIdleTimer()

    // Trailing debounce: each anchor-affecting mutation pushes the rebuild
    // back out so it runs against the latest stable DOM, instead of firing
    // mid-churn (e.g. a widget removing then re-adding its own subtree).
    if (this.#contentReinitTimer) {
      window.clearTimeout(this.#contentReinitTimer)
    }

    this.#contentReinitTimer = window.setTimeout(() => {
      this.#contentReinitTimer = null
      this.#queueInit()
    }, CONTENT_MUTATION_DEBOUNCE)
  }

  /**
   * Bounded rescans that catch anchors added after the content observer has
   * gone idle (see `#resetContentObserverIdleTimer`), most notably a
   * slow-hydrating Judge.me reviews root. Scheduled once per element
   * lifetime; does not create another `MutationObserver`.
   * @param {HTMLElement} main
   */
  #scheduleFallbackRescans(main) {
    if (this.#fallbackRescansScheduled) return
    this.#fallbackRescansScheduled = true

    const rescan = () => {
      const hadEntries = this.#entries.length > 0
      this.#buildLinks(main)

      if (this.#entries.length === 0) {
        this.#setVisible(false)
        return
      }

      if (!hadEntries) {
        if (this.#needsGeometry()) this.#measureRevealThreshold()
        this.#observeLayout(main)
      }

      this.#queueUpdate()
    }

    // Two rAFs let post-hydration layout (fonts, images, app widgets) settle
    // before the first timed rescan.
    requestAnimationFrame(() => requestAnimationFrame(rescan))

    for (const delay of FALLBACK_RESCAN_DELAYS_MS) {
      this.#fallbackRescanTimers.push(window.setTimeout(rescan, delay))
    }
  }

  /**
   * Keep watching only until the page settles: each anchor-relevant change
   * resets the idle timer, and after a quiet period we disconnect the content
   * observer (the `ResizeObserver` still handles later height changes).
   */
  #resetContentObserverIdleTimer() {
    if (this.#contentObserverIdleTimer) {
      window.clearTimeout(this.#contentObserverIdleTimer)
    }

    this.#contentObserverIdleTimer = window.setTimeout(() => {
      this.#contentObserverIdleTimer = null
      this.#contentObserver?.disconnect()
      this.#contentObserver = null
    }, CONTENT_OBSERVER_IDLE_TIMEOUT)
  }

  /**
   * Keep visibility and active state synchronized when content height changes.
   * @param {HTMLElement} main
   */
  #observeLayout(main) {
    if (!this.#resizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (isPageScrollLocked()) return
        this.#revealScrollY = null
        this.#queueUpdate()
      })
    }

    this.#resizeObserver.observe(this)
    this.#resizeObserver.observe(main)

    if (this.#viewport) {
      this.#resizeObserver.observe(this.#viewport)
    }

    const triggerSection = this.#getTriggerSection()
    if (triggerSection) {
      this.#resizeObserver.observe(triggerSection)
    }

    for (const entry of this.#entries) {
      if (entry.target) this.#resizeObserver.observe(entry.target)
    }
  }

  #updateState() {
    if (this.#entries.length === 0) {
      this.#setVisible(false)
      return
    }

    if (!this.#needsGeometry()) return

    const isVisible = this.#shouldBeVisible()
    this.#setVisible(isVisible)
    if (!isVisible) return

    const previousActiveId = this.#activeId
    const activeId =
      this.#getActiveIdFromScroll() || this.#getActiveIdFromHash()
    if (!activeId) return

    const activeChanged = previousActiveId !== activeId
    if (activeChanged) {
      this.#trackPositionDirty = false
    }

    if (this.#isTrackInteracting && previousActiveId && activeChanged) {
      this.#activeChangedDuringTrackInteraction = true
    }

    const ensureVisible =
      !this.#isTrackInteracting && (activeChanged || !this.#trackPositionDirty)

    const behavior =
      this.#syncLinkScrollWithoutMotion || this.#prefersReducedMotion()
        ? 'auto'
        : 'smooth'
    this.#syncLinkScrollWithoutMotion = false

    this.#setActive(activeId, {
      ensureVisible,
      behavior
    })
  }

  /**
   * @returns {boolean}
   */
  #shouldBeVisible() {
    if (this.#revealScrollY === null) {
      this.#measureRevealThreshold()
    }

    // Default to always showing if we can't find a trigger section, instead of hiding entirely.
    if (this.#revealScrollY === null) return true

    const showThreshold = this.#revealScrollY
    const hideThreshold = Math.max(0, showThreshold - VISIBILITY_HYSTERESIS)
    const scrollY = getPageScrollY()

    if (this.hidden || this.hasAttribute('hidden')) {
      return scrollY >= showThreshold
    }

    return scrollY >= hideThreshold
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    // Only toggle if the state has changed to prevent DOM thrashing
    const isCurrentlyHidden = this.hasAttribute('hidden')
    if (isCurrentlyHidden === !visible) return

    this.hidden = !visible
    if (visible) {
      this.removeAttribute('hidden')
      if ('inert' in this) {
        this.removeAttribute('inert')
      }
      this.setAttribute('aria-hidden', 'false')
    } else {
      this.setAttribute('hidden', '')
      if ('inert' in this) {
        this.setAttribute('inert', '')
      }
      this.setAttribute('aria-hidden', 'true')
      this.#trackPositionDirty = false
      this.#clearActive()
    }
  }

  /**
   * @returns {string | null}
   */
  #getActiveIdFromScroll() {
    if (this.#entries.length === 0) return null

    const isAtPageBottom =
      window.innerHeight + getPageScrollY() >=
      document.documentElement.scrollHeight - PAGE_END_THRESHOLD

    if (isAtPageBottom) {
      return this.#entries[this.#entries.length - 1]?.id || null
    }

    const activationLineY = Math.max(
      this.#getHeaderOffset(),
      window.innerHeight * (1 - ACTIVE_SECTION_LINE_FROM_BOTTOM)
    )
    let activeId = this.#entries[0]?.id || null

    for (const entry of this.#entries) {
      if (!entry.target || !entry.target.isConnected) continue

      const targetTop = entry.target.getBoundingClientRect().top

      if (targetTop <= activationLineY) {
        activeId = entry.id
        continue
      }

      break
    }

    return activeId
  }

  /**
   * @returns {string | null}
   */
  #getActiveIdFromHash() {
    const hash = window.location.hash.replace(/^#/, '')
    if (!hash) return null

    const matchingEntry = this.#entries.find((entry) => entry.id === hash)
    return matchingEntry?.id || null
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} target
   */
  #handleClick(event, target) {
    event.preventDefault()

    if (!target.isConnected) return
    if (this.#trackInteractionTimer) {
      window.clearTimeout(this.#trackInteractionTimer)
      this.#trackInteractionTimer = null
    }
    this.#isTrackInteracting = false
    this.#activeChangedDuringTrackInteraction = false
    this.#trackPositionDirty = false

    const top =
      target.getBoundingClientRect().top +
      getPageScrollY() -
      this.#getHeaderOffset() -
      ANCHOR_CLICK_EXTRA_VIEWPORT_INSET

    this.#setActive(target.id, {
      ensureVisible: true,
      behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth'
    })

    history.replaceState(null, '', `#${target.id}`)

    window.scrollTo({
      top,
      behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth'
    })

    const settlePageScroll = () => {
      if (this.#pageScrollTimer) {
        window.clearTimeout(this.#pageScrollTimer)
      }

      this.#pageScrollTimer = window.setTimeout(() => {
        this.#pageScrollTimer = null
        window.removeEventListener('scroll', settlePageScroll)

        this.#queueUpdate()
      }, 140)
    }

    window.addEventListener('scroll', settlePageScroll, { passive: true })
    settlePageScroll()
  }

  /**
   * @param {string} id
   * @param {{ ensureVisible?: boolean, behavior?: ScrollBehavior }} [options]
   */
  #setActive(id, options = {}) {
    const { ensureVisible = true, behavior = 'auto' } = options

    let activeEntry = null

    for (const entry of this.#entries) {
      const isActive = entry.id === id
      if (isActive !== entry.link.classList.contains(ACTIVE_CLASS)) {
        entry.link.classList.toggle(ACTIVE_CLASS, isActive)
      }

      if (isActive) {
        activeEntry = entry
        entry.link.setAttribute('aria-current', 'true')
      } else {
        entry.link.removeAttribute('aria-current')
      }
    }

    if (!activeEntry) return

    this.#activeId = activeEntry.id

    if (ensureVisible) {
      this.#ensureLinkVisible(activeEntry.link, behavior)
    }
  }

  #clearActive() {
    for (const entry of this.#entries) {
      entry.link.classList.remove(ACTIVE_CLASS)
      entry.link.removeAttribute('aria-current')
    }

    this.#activeId = null
  }

  /**
   * @param {ScrollBehavior} behavior
   */
  #ensureActiveLinkVisible(behavior = 'auto') {
    const activeEntry = this.#entries.find(
      (entry) => entry.id === this.#activeId
    )
    if (!activeEntry) return
    this.#ensureLinkVisible(activeEntry.link, behavior)
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {ScrollBehavior} behavior
   */
  #ensureLinkVisible(link, behavior) {
    if (!this.#viewport || this.#isTrackInteracting) return

    const viewportRect = this.#viewport.getBoundingClientRect()
    const linkRect = link.getBoundingClientRect()
    const padding = 12
    const isFullyVisible =
      linkRect.left >= viewportRect.left + padding &&
      linkRect.right <= viewportRect.right - padding

    if (isFullyVisible) return

    const maxScrollLeft =
      this.#viewport.scrollWidth - this.#viewport.clientWidth
    const desiredScrollLeft =
      this.#viewport.scrollLeft +
      (linkRect.left - viewportRect.left) -
      (viewportRect.width - linkRect.width) / 2

    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, desiredScrollLeft)
    )
    this.#startTrackAutoScroll(behavior)
    this.#viewport.scrollTo({ left: nextScrollLeft, behavior })
  }

  /**
   * @param {ScrollBehavior} behavior
   */
  #startTrackAutoScroll(behavior) {
    if (this.#trackAutoScrollTimer) {
      window.clearTimeout(this.#trackAutoScrollTimer)
    }

    const duration = behavior === 'smooth' ? 220 : 40

    this.#trackAutoScrollTimer = window.setTimeout(() => {
      this.#trackAutoScrollTimer = null
    }, duration)
  }

  /**
   * @returns {number}
   */
  #getHeaderGroupHeight() {
    // critical.js / header.js write the value inline on <body>; reading it there
    // avoids a document-wide style recalc on every scroll-driven update.
    const inline = document.body.style.getPropertyValue('--header-group-height')
    const raw =
      inline ||
      getComputedStyle(document.body).getPropertyValue('--header-group-height')
    return parseFloat(raw) || 0
  }

  /**
   * @returns {number}
   */
  #getHeaderOffset() {
    return this.#getHeaderGroupHeight() + HEADER_BUFFER
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getTriggerSection() {
    const sectionClass =
      this.dataset.productSectionClass || 'product-information'
    const main =
      document.querySelector('main[data-pdp-version]') ||
      document.querySelector('#MainContent')

    if (!(main instanceof HTMLElement)) return null

    for (const child of main.children) {
      if (!(child instanceof HTMLElement)) continue
      if (!child.classList.contains('shopify-section')) continue

      const candidate = child.querySelector(`.${sectionClass}`)
      if (candidate instanceof HTMLElement) {
        return child
      }
    }

    return null
  }

  #measureRevealThreshold() {
    // Reveal timing is driven by the same activation geometry as the
    // "active link" logic: the first entry is considered active when its
    // top edge reaches `activationLineY` (20% from the bottom of the viewport).
    const firstEntry = this.#entries[0]
    const firstTarget = firstEntry?.target
    if (!(firstTarget instanceof HTMLElement)) {
      // If we can't measure (unexpected DOM state), default to always visible
      // rather than hiding the bar indefinitely.
      this.#revealScrollY = null
      return
    }

    const activationLineY = Math.max(
      this.#getHeaderOffset(),
      window.innerHeight * (1 - ACTIVE_SECTION_LINE_FROM_BOTTOM)
    )

    const firstTop = firstTarget.getBoundingClientRect().top + getPageScrollY()
    this.#revealScrollY = Math.max(0, firstTop - activationLineY)
  }

  /**
   * @returns {boolean}
   */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}

if (!customElements.get('product-anchor-nav')) {
  customElements.define('product-anchor-nav', ProductAnchorNav)
}
